import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environment/environment';
import type { ApiResponse, ApiResponsePageableCustom } from '../interfaces/application';
import { buildHttpParams } from '../shared/utils';
import type { Observable } from 'rxjs';
import type { FinancialChurn, FinancialChurnDetailed, FinancialDepositAndWithdrawChart, FinancialDepositAndWithdrawMetrics, FinancialExpensesByCategory, FinancialInvoice, FinancialLifeCycle, FinancialProfitAndExpenses, FinancialSupportMetrics, SummaryDepositsMetrics, SummaryGgrDetailsDaily, SummaryGgrDetailsMonthly, SummaryGgrDetailsOperations, SummaryGgrDetailsTurnover, SummaryGgrDetailsWeekly, TopCasinoGamesTop5, TopSportsMarketsTop5, DepositDailyMetricsResponse, FtdDailyMetricsResponse } from '../interfaces/dashboard';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  protected readonly _http: HttpClient = inject(HttpClient);
  protected readonly _path: string = 'dashboard';

  public summaryDeposits(filters?: any): Observable<ApiResponse<SummaryDepositsMetrics>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<SummaryDepositsMetrics>>(`${environment.api}/${this._path}/deposits/summary-deposits`, { params });
  }

  public ggr(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/operations/ggr`, { params });
  }

  public summaryGgrDetailsDaily(filters?: any): Observable<ApiResponse<SummaryGgrDetailsDaily>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<SummaryGgrDetailsDaily>>(`${environment.api}/${this._path}/operations/summary-ggr-details/daily`, { params });
  }

  public summaryGgrDetailsWeekly(filters?: any): Observable<ApiResponse<SummaryGgrDetailsWeekly>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<SummaryGgrDetailsWeekly>>(`${environment.api}/${this._path}/operations/summary-ggr-details/weekly`, { params });
  }

  public summaryGgrDetailsMonthly(filters?: any): Observable<ApiResponse<SummaryGgrDetailsMonthly>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<SummaryGgrDetailsMonthly>>(`${environment.api}/${this._path}/operations/summary-ggr-details/monthly`, { params });
  }

  public summaryGgrDetailsTurnover(filters?: any): Observable<ApiResponse<SummaryGgrDetailsTurnover>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<SummaryGgrDetailsTurnover>>(`${environment.api}/${this._path}/operations/summary-ggr-details/turnover`, { params });
  }

  public summaryGgrDetailsOperations(filters?: any): Observable<ApiResponse<SummaryGgrDetailsOperations>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<SummaryGgrDetailsOperations>>(`${environment.api}/${this._path}/operations/summary-ggr-details/operations`, { params });
  }

  public summaryFinancialLifeCycle(filters?: any): Observable<ApiResponse<FinancialLifeCycle>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialLifeCycle>>(`${environment.api}/${this._path}/operations/life-cycle`, { params });
  }

  public summaryFinancialChurn(filters?: any): Observable<ApiResponse<FinancialChurn>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialChurn>>(`${environment.api}/${this._path}/operations/churn`, { params });
  }

  public summaryFinancialChurnDetailed(filters?: any): Observable<ApiResponsePageableCustom<FinancialChurnDetailed>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponsePageableCustom<FinancialChurnDetailed>>(`${environment.api}/${this._path}/operations/churn-detailed`, { params });
  }

  public topCasinoGamesTop5(filters?: any): Observable<ApiResponse<TopCasinoGamesTop5>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<TopCasinoGamesTop5>>(`${environment.api}/${this._path}/operations/top-casino-games/top5`, { params });
  }

  public topSportsMarketsTop5(filters?: any): Observable<ApiResponse<TopSportsMarketsTop5>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<TopSportsMarketsTop5>>(`${environment.api}/${this._path}/operations/top-sports-markets/top5`, { params });
  }

  public financialDepositAndWithdrawChart(filters?: any): Observable<ApiResponse<FinancialDepositAndWithdrawChart>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialDepositAndWithdrawChart>>(`${environment.api}/${this._path}/financial/deposit-and-withdrawal`, { params });
  }

  public financialDepositAndWithdrawMetrics(filters?: any): Observable<ApiResponse<FinancialDepositAndWithdrawMetrics>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialDepositAndWithdrawMetrics>>(`${environment.api}/${this._path}/financial/deposit-and-withdrawal-metrics`, { params });
  }

  public financialExpensesByCategory(filters?: any): Observable<ApiResponse<FinancialExpensesByCategory>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialExpensesByCategory>>(`${environment.api}/${this._path}/financial/expenses-by-category`, { params });
  }

  public financialInvoices(filters?: any): Observable<ApiResponse<FinancialInvoice>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialInvoice>>(`${environment.api}/${this._path}/financial/invoices`, { params });
  }

  public financialProfitAndExpenses(filters?: any): Observable<ApiResponse<FinancialProfitAndExpenses>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialProfitAndExpenses>>(`${environment.api}/${this._path}/financial/profit-and-expenses`, { params });
  }

  public financialSupportMetrics(filters?: any): Observable<ApiResponse<FinancialSupportMetrics>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<FinancialSupportMetrics>>(`${environment.api}/${this._path}/financial/support-metrics`, { params });
  }

  public depositDailyMetrics(filters?: any): Observable<DepositDailyMetricsResponse> {
    const params = buildHttpParams(filters);
    return this._http.get<DepositDailyMetricsResponse>(`${environment.api}/deposit-daily-metrics/daily`, { params });
  }

  public ftdDailyMetrics(filters?: any): Observable<FtdDailyMetricsResponse> {
    const params = buildHttpParams(filters);
    return this._http.get<FtdDailyMetricsResponse>(`${environment.api}/ftd-daily-metrics/daily`, { params });
  }
}
